/**
 * Utilities for syncing Vue component filter state with URL query parameters,
 * enabling browser back/forward navigation to restore filter state.
 */

/**
 * Serialize a filters object into a URL query params object.
 * - Fields listed in `arrayFields` are joined with ','
 * - Boolean false and empty strings/arrays are omitted
 * - All other truthy values are converted to strings
 *
 * @param {Object} filters        - The filters object from the component
 * @param {string[]} arrayFields  - List of field names that hold array values
 * @returns {Object}              - Plain query object suitable for $router.push({ query })
 */
export function buildQueryFromFilters(filters, arrayFields = []) {
  const query = {}
  Object.entries(filters).forEach(([key, value]) => {
    if (arrayFields.includes(key)) {
      if (Array.isArray(value) && value.length) {
        query[key] = value.join(',')
      }
    } else if (value !== null && value !== undefined && value !== '' && value !== false) {
      query[key] = String(value)
    }
  })
  return query
}

/**
 * Push a new query to the router only if it differs from the current route query.
 * Silently swallows NavigationDuplicated errors.
 *
 * @param {import('vue-router').default} router  - The Vue Router instance ($router)
 * @param {import('vue-router').Route}   route   - The current route object ($route)
 * @param {Object}                       query   - The new query object to push
 */
export function pushQueryIfChanged(router, route, query) {
  if (JSON.stringify(route.query) !== JSON.stringify(query)) {
    router.push({ query }).catch(() => {})
  }
}

/**
 * Restore filter fields from URL query parameters.
 * Only fields present in `fieldMap` are touched; missing query params leave filters unchanged.
 *
 * Field type values:
 *   'string'  — value is used as-is (default)
 *   'array'   — value is split on ',' into an array
 *   'boolean' — string 'true' becomes true, anything else becomes false
 *
 * @param {Object} query    - The current route query object ($route.query)
 * @param {Object} filters  - The component's filters object (mutated in place)
 * @param {Object} fieldMap - Map of { fieldName: 'string' | 'array' | 'boolean' }
 * @returns {boolean}       - true if any field was restored, false if query was empty
 */
export function restoreFiltersFromQuery(query, filters, fieldMap) {
  if (!query || !Object.keys(query).length) return false
  Object.entries(fieldMap).forEach(([field, type]) => {
    if (query[field] === undefined) return
    if (type === 'array') {
      filters[field] = query[field].split(',')
    } else if (type === 'boolean') {
      filters[field] = query[field] === 'true'
    } else {
      filters[field] = query[field]
    }
  })
  return true
}
